// Time preference windows for bookings without a fixed appointment time.
// Mirrors the backend enum + windows (Go: TimePreference / TimePreferenceWindow),
// see docs/appt/assign-mitra/backend/New Flow Mitra - Time Range Preferences.md.
// Hours are WIB (Asia/Jakarta). The backend does NOT validate the window — these
// values are display-only on the admin portal.

import { format, type Locale } from "date-fns";

export const TIME_PREFERENCES = {
	PAGI: { startHour: 8, endHour: 11 },
	SIANG: { startHour: 11, endHour: 15 },
	SORE: { startHour: 15, endHour: 18 },
	MALAM: { startHour: 18, endHour: 21 },
} as const;

export type TimePreference = keyof typeof TIME_PREFERENCES;

export function isTimePreference(value: unknown): value is TimePreference {
	return typeof value === "string" && value in TIME_PREFERENCES;
}

const pad = (value: number) => String(value).padStart(2, "0");

export function timePreferenceWindow(preference: TimePreference): {
	start: string;
	end: string;
} {
	const { startHour, endHour } = TIME_PREFERENCES[preference];
	return { start: `${pad(startHour)}:00`, end: `${pad(endHour)}:00` };
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

// Locale-aware formatting for the window bounds. Mirrors the app convention
// (see formatDateTime in Orders.tsx): Indonesian always uses 24h; other locales
// follow the user's time format, where English defaults to 12h AM/PM.
export type TimeWindowFormatOptions = {
	locale?: Locale;
	/** date-fns time token for non-id locales, e.g. "hh:mm a" or "HH:mm". */
	timeFormat?: string;
};

function formatWindowBounds(
	preference: TimePreference,
	options?: TimeWindowFormatOptions,
): { start: string; end: string } {
	const { startHour, endHour } = TIME_PREFERENCES[preference];
	const locale = options?.locale;

	// Fall back to the raw 24h strings when no locale is provided.
	if (!locale) return timePreferenceWindow(preference);

	const token =
		locale.code === "id" ? "HH:mm" : (options?.timeFormat ?? "hh:mm a");
	// Synthetic local-time dates — only the time-of-day is formatted, so the
	// calendar date and timezone are irrelevant.
	const at = (hour: number) =>
		format(new Date(2000, 0, 1, hour, 0, 0), token, { locale });

	return { start: at(startHour), end: at(endHour) };
}

/**
 * Localized window label, e.g. "Pagi (08:00 AM–11:00 AM WIB)" / "Pagi (08:00–11:00 WIB)".
 * `t` must be bound to the "appointments" i18n namespace.
 */
export function timePreferenceWindowLabel(
	preference: TimePreference,
	t: Translate,
	options?: TimeWindowFormatOptions,
): string {
	const { start, end } = formatWindowBounds(preference, options);
	const label = t(`time_preferences.${preference.toLowerCase()}`);
	return t("time_preferences.window", { label, start, end });
}

/**
 * Compact, color-less window for dense table cells, e.g. "Pagi · 08:00 AM–11:00 AM".
 * `t` must be bound to the "appointments" i18n namespace.
 */
export function timePreferenceShortWindow(
	preference: TimePreference,
	t: Translate,
	options?: TimeWindowFormatOptions,
): string {
	const { start, end } = formatWindowBounds(preference, options);
	const label = t(`time_preferences.${preference.toLowerCase()}`);
	return `${label} · ${start}–${end}`;
}

// Distinct badge color per window, following the shared pattern in
// app/frontend/lib/orders/status-styles.ts.
export const TIME_PREFERENCE_STYLES: Record<TimePreference, string> = {
	PAGI: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100/80",
	SIANG: "bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100/80",
	SORE: "bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100/80",
	MALAM:
		"bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-100/80",
};
